import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  MessageSquare, 
  Download, 
  ChevronDown, 
  ChevronUp,
  Search,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

// Mock data for orders
const mockOrders = [
  {
    id: 'ORD-123456',
    date: '2023-06-15',
    contracts: [
      {
        id: '1',
        name: 'Freelance Overeenkomst',
        price: 450,
      }
    ],
    totalPrice: 540, // Including VAT
    status: 'completed',
    steps: [
      { name: 'Bestelling geplaatst', date: '2023-06-15', completed: true },
      { name: 'Vragenlijst ingevuld', date: '2023-06-16', completed: true },
      { name: 'Contract in voorbereiding', date: '2023-06-18', completed: true },
      { name: 'Contract afgerond', date: '2023-06-20', completed: true },
    ],
    documents: [
      { name: 'Freelance Overeenkomst - Definitief.pdf', date: '2023-06-20', size: '1.2 MB' },
      { name: 'Factuur ORD-123456.pdf', date: '2023-06-20', size: '0.5 MB' },
    ],
    messages: 2,
  },
  {
    id: 'ORD-234567',
    date: '2023-06-22',
    contracts: [
      {
        id: '3',
        name: 'Geheimhoudingsovereenkomst (NDA)',
        price: 375,
      }
    ],
    totalPrice: 450, // Including VAT
    status: 'in_progress',
    steps: [
      { name: 'Bestelling geplaatst', date: '2023-06-22', completed: true },
      { name: 'Vragenlijst ingevuld', date: '2023-06-23', completed: true },
      { name: 'Contract in voorbereiding', date: '2023-06-24', completed: true },
      { name: 'Contract afgerond', date: null, completed: false },
    ],
    documents: [],
    messages: 1,
  },
  {
    id: 'ORD-345678',
    date: '2023-06-25',
    contracts: [
      {
        id: '5',
        name: 'Arbeidsovereenkomst',
        price: 550,
      },
      {
        id: '16',
        name: 'Geheimhoudingsovereenkomst',
        price: 325,
      }
    ],
    totalPrice: 1050, // Including VAT
    status: 'pending',
    steps: [
      { name: 'Bestelling geplaatst', date: '2023-06-25', completed: true },
      { name: 'Vragenlijst ingevuld', date: null, completed: false },
      { name: 'Contract in voorbereiding', date: null, completed: false },
      { name: 'Contract afgerond', date: null, completed: false },
    ],
    documents: [],
    messages: 0,
  },
];

// Status mapping for display
const statusMap = {
  pending: { label: 'In afwachting', color: 'bg-yellow-100 text-yellow-800' },
  in_progress: { label: 'In behandeling', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Voltooid', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Geannuleerd', color: 'bg-red-100 text-red-800' },
};

const OrderTracking: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateSort, setDateSort] = useState<'asc' | 'desc'>('desc');
  
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchOrders = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setOrders(mockOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [user]);
  
  const toggleOrderExpand = (orderId: string) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };
  
  const toggleDateSort = () => {
    setDateSort(dateSort === 'asc' ? 'desc' : 'asc');
  };
  
  // Filter and sort orders
  const filteredOrders = orders
    .filter(order => {
      // Search filter
      const searchMatch = 
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.contracts.some((contract: any) => 
          contract.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      // Status filter
      const statusMatch = statusFilter ? order.status === statusFilter : true;
      
      return searchMatch && statusMatch;
    })
    .sort((a, b) => {
      // Date sort
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      return dateSort === 'asc' ? dateA - dateB : dateB - dateA;
    });
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Mijn bestellingen</h1>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-grow max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Zoek een bestelling..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-gray-400 mr-2" />
              <select
                className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="">Alle statussen</option>
                <option value="pending">In afwachting</option>
                <option value="in_progress">In behandeling</option>
                <option value="completed">Voltooid</option>
                <option value="cancelled">Geannuleerd</option>
              </select>
            </div>
            
            <button
              onClick={toggleDateSort}
              className="flex items-center border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Datum {dateSort === 'asc' ? 'oplopend' : 'aflopend'}
              {dateSort === 'asc' ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-4">Geen bestellingen gevonden</h2>
          <p className="text-gray-600 mb-6">
            U heeft nog geen bestellingen geplaatst of er zijn geen bestellingen die aan uw zoekcriteria voldoen.
          </p>
          <Link
            to="/contracts"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300"
          >
            Bekijk de catalogus
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div 
                className="p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between cursor-pointer"
                onClick={() => toggleOrderExpand(order.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center mb-4 md:mb-0">
                  <div className="font-semibold text-lg text-blue-700 mr-4">{order.id}</div>
                  <div className="text-gray-500">
                    {format(new Date(order.date), 'dd MMMM yyyy', { locale: nl })}
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMap[order.status as keyof typeof statusMap].color}`}>
                      {statusMap[order.status as keyof typeof statusMap].label}
                    </span>
                  </div>
                  
                  <div className="font-medium">{order.totalPrice} €</div>
                  
                  <button
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-expanded={expandedOrder === order.id}
                    aria-label="Toggle order details"
                  >
                    {expandedOrder === order.id ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              {expandedOrder === order.id && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Bestelgegevens</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Bestelde contracten</h4>
                          <ul className="mt-2 space-y-2">
                            {order.contracts.map((contract: any) => (
                              <li key={contract.id} className="flex justify-between">
                                <span className="text-gray-800">{contract.name}</span>
                                <span className="text-gray-800">{contract.price} €</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex justify-between">
                            <span className="font-medium">Totaal (incl. BTW)</span>
                            <span className="font-medium">{order.totalPrice} €</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Bestelstatus</h3>
                      
                      <div className="relative">
                        <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200"></div>
                        
                        <ul className="space-y-6">
                          {order.steps.map((step: any, index: number) => (
                            <li key={index} className="relative pl-10">
                              <div className={`absolute left-0 top-1 h-8 w-8 rounded-full flex items-center justify-center ${
                                step.completed 
                                  ? 'bg-green-100 text-green-600' 
                                  : 'bg-gray-100 text-gray-400'
                              }`}>
                                {step.completed ? (
                                  <CheckCircle className="h-5 w-5" />
                                ) : (
                                  <Clock className="h-5 w-5" />
                                )}
                              </div>
                              
                              <div>
                                <p className="font-medium text-gray-900">{step.name}</p>
                                {step.date ? (
                                  <p className="text-sm text-gray-500">
                                    {format(new Date(step.date), 'dd MMMM yyyy', { locale: nl })}
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-500">In afwachting</p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {order.documents.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Documenten</h3>
                          
                          <ul className="space-y-3">
                            {order.documents.map((doc: any, index: number) => (
                              <li key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                <div className="flex items-center">
                                  <FileText className="h-5 w-5 text-blue-600 mr-3" />
                                  <div>
                                    <p className="font-medium text-gray-900">{doc.name}</p>
                                    <p className="text-sm text-gray-500">
                                      {format(new Date(doc.date), 'dd/MM/yyyy')} · {doc.size}
                                    </p>
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-800">
                                  <Download className="h-5 w-5" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Acties</h3>
                        
                        <div className="space-y-3">
                          {order.status === 'pending' && !order.steps[1].completed && (
                            <Link
                              to={`/questionnaire/${order.id}`}
                              className="flex items-center justify-center w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-300"
                            >
                              Vragenlijst invullen
                            </Link>
                          )}
                          
                          <Link
                            to={`/messages?order=${order.id}`}
                            className="flex items-center justify-center w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium py-2 px-4 rounded-md transition duration-300"
                          >
                            <MessageSquare className="h-5 w-5 mr-2" />
                            Contact met jurist
                            {order.messages > 0 && (
                              <span className="ml-2 bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                {order.messages}
                              </span>
                            )}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderTracking;